/**
 * Timeline - Chronological events
 *
 * Usage:
 *   <Timeline
 *     title="Today's Activities"
 *     data={{
 *       events: [
 *         { time: '9:00 AM', title: 'Morning standup', type: 'meeting' },
 *         { time: '10:30 AM', title: 'Code review', subtitle: 'PR #123', type: 'work' },
 *         { time: '12:00 PM', title: 'Lunch', type: 'break' },
 *       ]
 *     }}
 *   />
 */

import type { BlockProps } from './types';
import { CHART_COLORS } from './colors';
import './blocks.css';

export interface TimelineData {
  events: Array<{
    id?: string;
    time: string;
    title: string;
    subtitle?: string;
    type?: string;
    icon?: string;
    color?: string;
    completed?: boolean;
  }>;
}

export interface TimelineProps extends BlockProps<TimelineData> {
  /** Compact mode */
  compact?: boolean;
  /** Event click handler */
  onEventClick?: (event: TimelineData['events'][0], index: number) => void;
}

const typeIcons: Record<string, string> = {
  meeting: '📅',
  work: '💻',
  break: '☕',
  exercise: '🏃',
  meal: '🍽️',
  sleep: '😴',
  travel: '🚗',
  call: '📞',
  email: '📧',
  task: '✅',
  default: '📌',
};

const typeColors: Record<string, string> = {
  meeting: CHART_COLORS[0],
  work: CHART_COLORS[4],
  break: CHART_COLORS[3],
  exercise: CHART_COLORS[2],
  meal: CHART_COLORS[3],
  default: CHART_COLORS[0],
};

export function Timeline({
  data,
  title,
  compact = false,
  onEventClick,
  className,
}: TimelineProps) {
  const { events } = data;

  if (!events || events.length === 0) {
    return (
      <div className={`block-table ${className || ''}`}>
        {title && <div className="block-table-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No events
        </div>
      </div>
    );
  }

  const getIcon = (event: TimelineData['events'][0]) => {
    if (event.icon) return event.icon;
    if (event.type && typeIcons[event.type]) return typeIcons[event.type];
    return typeIcons.default;
  };

  const getColor = (event: TimelineData['events'][0]) => {
    if (event.color) return event.color;
    if (event.type && typeColors[event.type]) return typeColors[event.type];
    return typeColors.default;
  };

  return (
    <div className={`timeline-block ${className || ''}`} style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {title && (
        <div style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 600,
          color: 'var(--color-text)',
          padding: 'var(--space-3) var(--space-4)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          {title}
        </div>
      )}

      <div style={{ padding: compact ? 'var(--space-2) var(--space-4)' : 'var(--space-3) var(--space-4)' }}>
        {events.map((event, index) => {
          const color = getColor(event);
          const isLast = index === events.length - 1;

          return (
            <div
              key={event.id || index}
              onClick={onEventClick ? () => onEventClick(event, index) : undefined}
              onKeyDown={onEventClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEventClick(event, index);
                }
              } : undefined}
              tabIndex={onEventClick ? 0 : undefined}
              role={onEventClick ? 'button' : undefined}
              aria-label={onEventClick ? `${event.title} at ${event.time}` : undefined}
              style={{
                display: 'flex',
                gap: 'var(--space-3)',
                cursor: onEventClick ? 'pointer' : undefined,
                paddingBottom: isLast ? 0 : compact ? 'var(--space-2)' : 'var(--space-3)',
                opacity: event.completed ? 0.6 : 1,
              }}
            >
              {/* Timeline line and dot */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '20px',
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {event.completed && (
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                {!isLast && (
                  <div style={{
                    width: '2px',
                    flex: 1,
                    backgroundColor: 'var(--color-border)',
                    marginTop: '4px',
                  }} />
                )}
              </div>

              {/* Time */}
              <div style={{
                fontSize: compact ? '11px' : '12px',
                fontWeight: 500,
                color: 'var(--color-text-muted)',
                width: compact ? '50px' : '70px',
                flexShrink: 0,
                paddingTop: '1px',
              }}>
                {event.time}
              </div>

              {/* Icon */}
              <span style={{ fontSize: compact ? '14px' : '16px' }}>
                {getIcon(event)}
              </span>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0, paddingTop: '1px' }}>
                <div style={{
                  fontSize: compact ? '13px' : '14px',
                  fontWeight: 500,
                  color: 'var(--color-text)',
                  textDecoration: event.completed ? 'line-through' : undefined,
                }}>
                  {event.title}
                </div>
                {event.subtitle && (
                  <div style={{
                    fontSize: compact ? '11px' : '12px',
                    color: 'var(--color-text-muted)',
                    marginTop: '2px',
                  }}>
                    {event.subtitle}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Timeline;
